import { useMemo } from 'react';
import { useFetch } from '../../../../../hooks';
import { getBatchVisitStats } from '../../../../../api';
import { extractShortId } from '../../../../url-sortener/utils/urlUtils';
import type { TaskDetails } from '../../../../../services/scraper';

type VisitStatsData = {
  last_visited_at?: string;
  visited_count?: number;
  isLoading: boolean;
  error: boolean;
};

type Filters = {
  exclude_countries?: string[];
  [key: string]: any;
};

export const useCampaignVisitStats = (
  tasks: TaskDetails[],
  filters?: Filters
) => {
  const taskKeys = useMemo(
    () =>
      tasks.map(task => ({
        id: task.id || '',
        demoUrl: task.demo_url,
        shortId: extractShortId(task.demo_url),
      })),
    [tasks]
  );

  const excludeCountries = useMemo(() => {
    const countries = filters?.exclude_countries;

    return countries && countries.length > 0 ? countries : undefined;
  }, [filters?.exclude_countries]);

  const { data: allStats, loading } = useFetch<Map<string, VisitStatsData>>(
    async () => {
      const statsMap = new Map<string, VisitStatsData>();

      const shortIdToTaskIdMap = new Map<string, string>();
      const validShortIds: string[] = [];

      taskKeys.forEach(({ id, shortId }) => {
        if (shortId && id) {
          shortIdToTaskIdMap.set(shortId, id);
          validShortIds.push(shortId);
        } else {
          statsMap.set(id, {
            isLoading: false,
            error: true,
          });
        }
      });

      if (validShortIds.length === 0) {
        return statsMap;
      }

      try {
        const batchResponse = await getBatchVisitStats(
          validShortIds,
          excludeCountries
        );
        const batchStats = batchResponse?.stats || [];

        batchStats.forEach(stat => {
          const taskId = stat.short_id
            ? shortIdToTaskIdMap.get(stat.short_id)
            : null;

          if (taskId) {
            statsMap.set(taskId, {
              last_visited_at: stat.last_visited_at,
              visited_count: stat.total_count,
              isLoading: false,
              error: false,
            });
          }
        });

        validShortIds.forEach(shortId => {
          const taskId = shortIdToTaskIdMap.get(shortId);
          if (taskId && !statsMap.has(taskId)) {
            statsMap.set(taskId, {
              isLoading: false,
              error: true,
            });
          }
        });
      } catch (error) {
        validShortIds.forEach(shortId => {
          const taskId = shortIdToTaskIdMap.get(shortId);
          if (taskId) {
            statsMap.set(taskId, {
              isLoading: false,
              error: true,
            });
          }
        });
      }

      return statsMap;
    },
    [taskKeys, excludeCountries],
    {
      skip: tasks.length === 0,
      initialData: new Map<string, VisitStatsData>(),
      onError: false,
    }
  );

  return useMemo(() => {
    const resultMap = new Map<string, VisitStatsData>();

    tasks.forEach(task => {
      const taskId = task.id || '';
      const existingStats = allStats?.get(taskId);

      if (existingStats) {
        resultMap.set(taskId, existingStats);
      } else if (loading) {
        resultMap.set(taskId, {
          isLoading: true,
          error: false,
        });
      } else {
        resultMap.set(taskId, {
          isLoading: false,
          error: true,
        });
      }
    });

    return resultMap;
  }, [tasks, allStats, loading]);
};
